import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

export function mobilePhoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isValidMobile = /^[4-9]\d{8}$/.test(control.value);

    if (isValidMobile) {
      return null;
    }

    return { invalid_mobile_phone_number: true };
  };
}

export function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const isValidMobile = /^[4-9]\d{8}$/.test(control.value);
    const isValidLandline = /^[2-9]\d{8}$/.test(control.value);

    if (isValidMobile || isValidLandline) {
      return null;
    }

    return { invalid_phone_number: true };
  };
}

export function emailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value;

    if (!email) {
      return null; // lub { required: true } jeśli ma być obowiązkowy
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    if (emailRegex.test(email)) {
      return null;
    }

    return { invalid_email: true };
  };
}

export function pastDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const inputValue = control.value;
    const inputDate = new Date(inputValue);

    if (inputValue && !isNaN(inputDate.getTime()) && inputDate < today) {
      return { past_date: true };
    }

    return null;
  };
}

export function futureDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const selectedDate = new Date(control.value);
    const currentDate = new Date();

    const timeDifference = selectedDate.getTime() - currentDate.getTime();
    const dayDifference = timeDifference / (1000 * 3600 * 24);

    if (dayDifference > 14) {
      return { max_future_date: true };
    }

    return null;
  };
}

export function customMaxValidator(
  max: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value && control.value > max) {
      const error: { [key: string]: any } = {};
      error[errorName] = { maxValue: max };
      return error;
    }
    return null;
  };
}

export function customMinValidator(
  min: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value && control.value < min) {
      const error: { [key: string]: any } = {};
      error[errorName] = { minValue: min };
      return error;
    }
    return null;
  };
}

export function customMinLengthValidator(
  minLength: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value as string;

    if (value && value.length < minLength) {
      const error: { [key: string]: any } = {};
      error[errorName] = { minLength: minLength };
      return error;
    }

    return null;
  };
}

export function customMinTrimmedLengthValidator(
  minLength: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    if (control.value === undefined) return null;
    const value = (control.value as string)?.trim();
    if (value !== undefined && value.length < minLength) {
      const error: { [key: string]: any } = {};
      error[errorName] = { minLength: minLength };
      return error;
    }

    return null;
  };
}

export function customMaxLengthValidator(
  maxLength: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value as string;

    if (value && value.length > maxLength) {
      const error: { [key: string]: any } = {};
      error[errorName] = { maxLength: maxLength };
      return error;
    }

    return null;
  };
}

export function sumOfFieldsValidator(
  fieldNames: string[],
  maxSum: number,
  errorName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const sum = fieldNames.reduce((acc, fieldName) => {
      const value = Number(control.get(fieldName)?.value) || 0;
      return acc + value;
    }, 0);

    const error: { [key: string]: any } = { [errorName]: { sum, maxSum } };

    if (sum > maxSum) {
      fieldNames.forEach((fieldName) => {
        const fieldControl = control.get(fieldName);
        if (fieldControl && !fieldControl.errors) {
          fieldControl.setErrors({ [errorName]: true });
        }
      });
      return error;
    } else {
      fieldNames.forEach((fieldName) => {
        const fieldControl = control.get(fieldName);
        if (
          fieldControl &&
          fieldControl.errors &&
          fieldControl.errors[errorName]
        ) {
          const { [errorName]: removed, ...otherErrors } = fieldControl.errors;
          fieldControl.setErrors(
            Object.keys(otherErrors).length ? otherErrors : null
          );
        }
      });
    }

    return null;
  };
}

export function parcelNotStandardCheckboxValidator(
  heightFieldName: string,
  lengthFieldName: string,
  widthFieldName: string,
  customElementFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const height = Number(control.get(heightFieldName)?.value || 0);
    const length = Number(control.get(lengthFieldName)?.value || 0);
    const width = Number(control.get(widthFieldName)?.value || 0);
    const customElementControl = control.get(customElementFieldName);

    const error: { [key: string]: any } | null = null;

    if (height > length || height > width) {
      if (customElementControl && !customElementControl.value) {
        customElementControl.setValue(true);
        customElementControl.disable();
      }
    } else {
      if (customElementControl && customElementControl.disabled) {
        customElementControl.enable();
        customElementControl.setValue(false);
      }
    }

    return error;
  };
}

export function ibanChecksumValidator(errorName: string): ValidatorFn {
  return (control: AbstractControl) => {
    const accountNumber = control.value;

    if (
      !accountNumber ||
      typeof accountNumber !== 'string' ||
      accountNumber.length !== 26
    ) {
      return null;
    }

    const rearrangedAccountNumber =
      accountNumber.slice(2) + '2521' + accountNumber.slice(0, 2);

    const numericAccountNumber = BigInt(rearrangedAccountNumber);

    if (numericAccountNumber % 97n !== 1n) {
      const error: { [key: string]: any } = {};
      error[errorName] = { invalidChecksum: true };
      return error;
    }

    return null;
  };
}

export function codAmountValidator(
  isCodFieldName: string,
  codAmountFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const isCod = control.get(isCodFieldName)?.value || false;
    const codAmountControl = control.get(codAmountFieldName);

    if (!codAmountControl) {
      return null;
    }

    if (isCod) {
      if (
        codAmountControl.disabled ||
        !codAmountControl.hasValidator(Validators.required)
      ) {
        codAmountControl.setValidators([
          Validators.required,
          customMaxValidator(10000, 'max_cod_amount'),
          customMinValidator(0, 'min_cod_amount'),
        ]);
        codAmountControl.enable({ emitEvent: false });
        codAmountControl.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      if (codAmountControl.enabled) {
        codAmountControl.clearValidators();
        if (codAmountControl.value !== undefined) {
          codAmountControl.setValue(undefined, { emitEvent: false });
        }
        codAmountControl.disable({ emitEvent: false });
        codAmountControl.markAsUntouched();
        codAmountControl.updateValueAndValidity({ emitEvent: false });
      }
    }

    return null;
  };
}

export function accountNumberValidator(
  isCodFieldName: string,
  accountNumberFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const isCod = control.get(isCodFieldName)?.value || false;
    const accountNumberControl = control.get(accountNumberFieldName);

    if (!accountNumberControl) {
      return null;
    }

    if (isCod) {
      if (
        accountNumberControl.disabled ||
        !accountNumberControl.hasValidator(Validators.required)
      ) {
        accountNumberControl.setValidators([
          Validators.required,
          customMinLengthValidator(26, 'min_length_account_number'),
          customMaxLengthValidator(26, 'max_length_account_number'),
          ibanChecksumValidator('iban_incorrect'),
        ]);
        accountNumberControl.enable({ emitEvent: false });
        accountNumberControl.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      if (accountNumberControl.enabled) {
        accountNumberControl.clearValidators();
        if (accountNumberControl.value !== undefined) {
          accountNumberControl.setValue(undefined, { emitEvent: false });
        }
        accountNumberControl.disable({ emitEvent: false });
        accountNumberControl.markAsUntouched();
        accountNumberControl.updateValueAndValidity({ emitEvent: false });
      }
    }

    return null;
  };
}

export function incAmountValidator(
  isCodFieldName: string,
  codAmountFieldName: string,
  isIncFieldName: string,
  incAmountFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const isInc = control.get(isIncFieldName)?.value || false;
    const incAmountControl = control.get(incAmountFieldName);

    if (!incAmountControl) {
      return null;
    }

    if (isInc) {
      if (
        incAmountControl.disabled ||
        !incAmountControl.hasValidator(Validators.required)
      ) {
        incAmountControl.setValidators([
          Validators.required,
          customMaxValidator(50000, 'max_inc_amount'),
        ]);
        incAmountControl.enable({ emitEvent: false });
        incAmountControl.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      if (incAmountControl.enabled) {
        if (incAmountControl.value !== undefined) {
          incAmountControl.setValue(undefined, { emitEvent: false });
        }
        incAmountControl.disable({ emitEvent: false });
        incAmountControl.markAsUntouched();
        incAmountControl.updateValueAndValidity({ emitEvent: false });
      }
    }

    return null;
  };
}

export function incMinValidator(
  isCodFieldName: string,
  codAmountFieldName: string,
  incAmountFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const incAmountControl = control.get(incAmountFieldName);
    const isCod = control.get(isCodFieldName)?.value || false;
    const codAmount = Number(control.get(codAmountFieldName)?.value) || 0;
    if (
      isCod &&
      incAmountControl &&
      incAmountControl.value &&
      incAmountControl.value < codAmount
    ) {
      const error: { [key: string]: any } = {};
      error['inc_amount_lower_than_cod_amount'] = { minValue: codAmount };
      incAmountControl?.setErrors(error);
    } else if (
      incAmountControl &&
      incAmountControl.value &&
      incAmountControl.value < 0
    ) {
      const error: { [key: string]: any } = {};
      error['min_inc_amount'] = { minValue: 0 };
      incAmountControl?.setErrors(error);
    }
    return null;
  };
}

export function sumParcelCountValidator(
  rowsFieldName: string,
  parcelCountFieldName: string,
  maxSumCount: number
): ValidatorFn {
  return (control: AbstractControl) => {
    const rows = control.get(rowsFieldName) as FormArray<FormGroup>;

    if (rows) {
      let parcelCountSum = 0;
      for (const formGroup of rows.controls) {
        parcelCountSum =
          parcelCountSum + Number(formGroup.get(parcelCountFieldName)?.value);
      }

      if (parcelCountSum > maxSumCount) {
        const error: { [key: string]: any } = {};
        error['MAX_SUM_PARCEL_COUNT'] = { maxValue: maxSumCount };
        rows.setErrors(error);
      }

      return null;
    }

    return null;
  };
}

export function parcelContentValidator(
  isIncFieldName: string,
  parcelContentFieldName: string
): ValidatorFn {
  return (control: AbstractControl) => {
    const isInc = control.get(isIncFieldName)?.value || false;
    const parcelContentControl = control.get(parcelContentFieldName);

    if (!parcelContentControl) {
      return null;
    }

    if (isInc) {
      if (parcelContentControl.disabled) {
        parcelContentControl.setValidators([
          Validators.required,
          customMinTrimmedLengthValidator(1, 'min_parcel_content_length'),
        ]);
        parcelContentControl.enable({ emitEvent: false });
        parcelContentControl.updateValueAndValidity({ emitEvent: false });
      }
    } else {
      if (parcelContentControl.enabled) {
        if (parcelContentControl.value !== undefined) {
          parcelContentControl.setValue(undefined, { emitEvent: false });
        }
        parcelContentControl.disable({ emitEvent: false });
        parcelContentControl.markAsUntouched();
        parcelContentControl.updateValueAndValidity({ emitEvent: false });
      }
    }

    return null;
  };
}
